import { useRouter } from 'next/navigation';
import { SuperTokensConfig } from 'supertokens-auth-react/lib/build/types';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import { EmailPasswordPreBuiltUI } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui';
import MultiFactorAuth from 'supertokens-auth-react/recipe/multifactorauth';
import { MultiFactorAuthPreBuiltUI } from 'supertokens-auth-react/recipe/multifactorauth/prebuiltui';
import Session from 'supertokens-auth-react/recipe/session';
import TOTP from 'supertokens-auth-react/recipe/totp';
import { TOTPPreBuiltUI } from 'supertokens-auth-react/recipe/totp/prebuiltui';

import { appInfo } from './app-info';

const routerInfo: { router?: ReturnType<typeof useRouter>; pathName?: string } =
  {};

export function setRouter(
  router: ReturnType<typeof useRouter>,
  pathName: string
) {
  routerInfo.router = router;
  routerInfo.pathName = pathName;
}

export const frontendConfig = (): SuperTokensConfig => {
  return {
    appInfo,
    style: `
      [data-supertokens~=container] {
        --palette-textTitle: var(--dp-blue-800);
        --palette-textLabel: var(--dp-blue-800);
        --palette-textPrimary: var(--dp-blue-800);
        --palette-error: var(--dp-red-800);
        --palette-textLink: var(--dp-blue-600);
        --palette-primary: var(--dp-orange-800);
      }
      [data-supertokens~=container] {
        font-family: var(--font-inter), sans-serif;
        letter-spacing: var(--dp-letter-spacing);
      }
      [data-supertokens~=input] {
        font-family: var(--font-inter), sans-serif;
        letter-spacing: var(--dp-letter-spacing);
      }
      [data-supertokens~=headerTitle] {
        font-family: var(--font-sora), sans-serif;
      }
      [data-supertokens~=superTokensBranding] {
        display: none;
      }
      [data-supertokens~=headerSubtitle] {
        display: none;
      }
      [data-supertokens~=button] {
        min-height: 54px;
      }
    `,
    recipeList: [
      EmailPassword.init({
        signInAndUpFeature: {
          signUpForm: {
            formFields: [
              {
                id: 'name',
                label: 'Full name',
                placeholder: 'First name and last name',
              },
              {
                id: 'token',
                label: '',
                getDefaultValue: () =>
                  sessionStorage.getItem('customerRegistrationToken') ??
                  new URLSearchParams(window.location.search).get('token') ??
                  '',
                inputComponent: ({ value, name, onChange }) => (
                  <div
                    data-supertokens="inputContainer"
                    style={{ display: 'none' }}
                  >
                    <div data-supertokens="inputWrapper">
                      <input
                        type="text"
                        className="supertokens-input"
                        data-supertokens="input input-token"
                        value={value}
                        name={name}
                        onChange={(e) => onChange(e.target.value)}
                        disabled
                      />
                    </div>
                  </div>
                ),
              },
              {
                id: 'invitationId',
                label: '',
                optional: true,
                getDefaultValue: () =>
                  sessionStorage.getItem('collaboratorInvitationToken') || '',
                inputComponent: ({ value, name, onChange }) => (
                  <div
                    data-supertokens="inputContainer"
                    style={{ display: 'none' }}
                  >
                    <div data-supertokens="inputWrapper">
                      <input
                        type="text"
                        className="supertokens-input"
                        data-supertokens="input input-invitationId"
                        value={value}
                        name={name}
                        onChange={(e) => onChange(e.target.value)}
                        disabled
                      />
                    </div>
                  </div>
                ),
              },
            ],
          },
        },
      }),
      TOTP.init(),
      MultiFactorAuth.init({
        firstFactors: [MultiFactorAuth.FactorIds.EMAILPASSWORD],
        getRedirectionURL: async (context) => {
          if (
            context.action === 'GO_TO_FACTOR' &&
            context.factorId === MultiFactorAuth.FactorIds.TOTP
          ) {
            return '/auth/mfa/totp';
          }
          return undefined;
        },
      }),
      Session.init({
        tokenTransferMethod: 'cookie',
      }),
    ],
    getRedirectionURL: async (context) => {
      if (context.action === 'SUCCESS' && context.newSessionCreated) {
        /*
         * A first-factor sign-in creates a session (newSessionCreated=true) even when a
         * second factor is still pending. Returning a URL here would pre-empt the
         * MultiFactorAuth GO_TO_FACTOR redirect, so we defer to it while MFA is incomplete.
         */
        const payload = await Session.getAccessTokenPayloadSecurely();
        const mfaCompleted =
          MultiFactorAuth.MultiFactorAuthClaim.getValueFromPayload(payload)?.v;
        if (mfaCompleted === false) {
          return undefined;
        }

        sessionStorage.removeItem('collaboratorInvitationToken');
        sessionStorage.removeItem('customerRegistrationToken');
        if (context.redirectToPath !== undefined) {
          // we are navigating back to where the user was before they authenticated
          return context.redirectToPath;
        }
        return '/';
      }
      return Promise.resolve(undefined);
    },
    windowHandler: (orig) => {
      return {
        ...orig,
        location: {
          ...orig.location,
          getPathName: () => routerInfo.pathName ?? '',
          assign: (url) => window.location.assign(url.toString()),
          setHref: (url) => window.location.assign(url.toString()),
        },
      };
    },
  };
};

export const PreBuiltUIList = [
  EmailPasswordPreBuiltUI,
  MultiFactorAuthPreBuiltUI,
  TOTPPreBuiltUI,
];
